import { useBoolean } from '@chakra-ui/hooks';
import { Center, Flex, Text } from '@wegroup/design-system';
import React, { useEffect, useState } from 'react';
import { Unsubscribe } from 'redux';
import ConditonalWrapper from '../../../../common/src/components/ConditionalWrapper';
import { FLOWS_DATABASE_NAME } from '../../../../common/src/constants/localForageDBNames';
import {
  useFlowsDispatch,
  useFlowsSelector,
} from '../../../../common/src/utils/globalVariables';
import LoadingSpinner from '../../../../common/src/components/LoadingSpinner';
import useSalesConversationResetState from '../../../../sales-conversation/src/hooks/useSalesConversationResetState';
import useRiskAnalysisResetState from '../../../../risk-analysis/src/hooks/useRiskAnalysisResetState';
import useFlowsResetState from '../../hooks/useFlowsResetState';
import { useFlowsRouteParams } from '../../hooks/useFlowsRouteParams';
import useSessionId from '../../hooks/useSessionId';
import { loadState, persistState } from '../../services/flowsStorageService';
import { RootState } from '../redux/reducers/rootReducer';
import {
  setFlowsMetaDataState,
  setInitializationError,
} from '../redux/slices/flowsMetaDataSlice';
import { setFlowsState } from '../redux/slices/flowsSlice';
import store from '../redux/store';
import useCompanyScanResetState from '../../../../company-scan/hooks/useCompanyScanResetState';
import flowsConfig, { defaultFlowsConfig } from '../../config/flowsConfig';
import useStartFlow from '../../hooks/useStartFlow';

const STORE_NAME = 'flows';
let storedListener: Unsubscribe | null = null;

interface Props {
  AppShell: React.FC<React.PropsWithChildren<{ isLoading?: boolean }>>;
}

export const saveStore = async (sessionId: string): Promise<void> => {
  await persistState<RootState>(
    FLOWS_DATABASE_NAME,
    STORE_NAME,
    sessionId,
    store.getState(),
  );
};

const FlowsStorageProvider: React.FC<React.PropsWithChildren<Props>> = ({
  AppShell,
  children,
}) => {
  // This is temp, session id should be moved as a query param everywhere
  const { sessionId: routeSessionId, flowId } = useFlowsRouteParams();
  const queryParamSessionId = useSessionId();

  const sessionId = queryParamSessionId || routeSessionId;

  const flowsDispatch = useFlowsDispatch();
  const initializationError = useFlowsSelector(
    (state) => state.flowsMetaData.initializationError,
  );

  // Reset states
  const { reset: louiseReset } = useSalesConversationResetState();
  const { reset: riskAnalysisReset } = useRiskAnalysisResetState();
  const { reset: companyScanReset } = useCompanyScanResetState();
  const { reset: flowsReset } = useFlowsResetState();

  const startFlow = useStartFlow();

  // State used to track the session id that is currently being listened to for store changes
  const [currentSessionIdSubscription, setCurrentSessionIdSubscription] =
    useState<string>();
  const [isInitialLoading, setIsInitialLoading] = useBoolean();

  /**
   * Load the store through indexedDB
   * @returns If the session exists it will return true
   */
  const loadStore = async (sessionId: string): Promise<boolean> => {
    const storedState = await loadState<RootState>(
      FLOWS_DATABASE_NAME,
      STORE_NAME,
      sessionId,
    );

    // All slices have to be hydrated separately
    if (storedState) {
      flowsDispatch(setFlowsState(storedState.flows));
      flowsDispatch(setFlowsMetaDataState(storedState.flowsMetaData));
      return true;
    }

    return false;
  };

  /**
   * This will clear any subscription and makes sure the new store is being subscribed to
   */
  const changeSession = async (sessionId: string) => {
    if (currentSessionIdSubscription !== sessionId) {
      // Executing this function unsubscribes from the listener if there is any
      storedListener?.();
      await saveStore(sessionId);
      storedListener = store.subscribe(async () => {
        await saveStore(sessionId);
      });
    }
  };

  /**
   * If a session is not found locally it can be retrieved and started again.
   * TODO: This function is temporary. This should be changed to a more generic
   * one so that it doesnt have to be updated for each new flow
   */
  const retrieveSession = (sessionId: string) => {
    if (flowId) startFlow({ flowId, sessionId });
  };

  /**
   * This will start the persistence service for the given session id.
   * It will do 3 things
   * - Check and load the current session state for the given session id
   * - Check if the session has changed since the previous session and do the necessary actions
   * - Update the current session id in the state
   */
  const startPersistenceService = async (sessionId: string) => {
    // Clear any previous initialization errors when starting a new session
    flowsDispatch(setInitializationError(undefined));

    const wasFound = await loadStore(sessionId);

    if (wasFound) {
      if (currentSessionIdSubscription !== sessionId) {
        await changeSession(sessionId);
      }
      setCurrentSessionIdSubscription(sessionId);
    } else {
      retrieveSession(sessionId);
      await changeSession(sessionId);
    }

    setIsInitialLoading.off();
  };

  const reset = () => {
    // Unscrubscribe redux listener on unmount
    storedListener?.();
    louiseReset();
    riskAnalysisReset();
    companyScanReset();
    flowsReset();
    flowsDispatch(setInitializationError(undefined));
  };

  useEffect(() => {
    // Only on initial render AND if there is a sessionId, start loading
    if (sessionId) setIsInitialLoading.on();

    return () => {
      reset();
    };
  }, []);

  useEffect(() => {
    if (sessionId) {
      startPersistenceService(sessionId);
    } else {
      // To prevent persistance of states in other tabs
      reset();
    }
  }, []);

  // Show error view if there's an initialization error
  if (initializationError) {
    if (flowId) {
      const ErrorView =
        (flowsConfig[flowId] || defaultFlowsConfig(flowId)).errorView ||
        defaultFlowsConfig(flowId).errorView;
      return <ErrorView error={initializationError} />;
    }

    // If no flowId, show a friendly error message
    return (
      <ConditonalWrapper
        condition={!!AppShell}
        wrapper={(children) =>
          AppShell && <AppShell isLoading>{children}</AppShell>
        }
      >
        <Center h="100%" w="100%" bg="white">
          <Flex
            maxW="4xl"
            flexDir="column"
            p={{ base: '3', tablet: '6' }}
            textAlign="center"
          >
            <Text textStyle="h3Headline" mb="4">
              {initializationError}
            </Text>
          </Flex>
        </Center>
      </ConditonalWrapper>
    );
  }

  if (isInitialLoading) {
    if (flowId) {
      const LoadingView =
        (flowsConfig[flowId] || defaultFlowsConfig(flowId)).loadingView ||
        defaultFlowsConfig(flowId).loadingView;
      return <LoadingView />;
    }

    return (
      <ConditonalWrapper
        condition={!!AppShell}
        wrapper={(children) =>
          AppShell && <AppShell isLoading>{children}</AppShell>
        }
      >
        <Center h="100%" w="100%">
          <LoadingSpinner id="FLOWS_FLOW-STORAGE-PROVIDER" />
        </Center>
      </ConditonalWrapper>
    );
  }

  return <>{children}</>;
};

export default FlowsStorageProvider;
